import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/PageParts";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/format";
import { RefreshCw, PlayCircle, Settings2, Zap, Clock, AlertTriangle, CheckCircle2 } from "lucide-react";

const STATUS_STYLE = {
  waiting_for_operator: "bg-amber-50 text-amber-800 border-amber-200",
  running: "bg-blue-50 text-blue-800 border-blue-200",
  retrying: "bg-blue-50 text-blue-800 border-blue-200",
  completed: "bg-emerald-50 text-emerald-800 border-emerald-200",
  failed: "bg-rose-50 text-rose-800 border-rose-200",
  pending: "bg-slate-100 text-slate-700 border-slate-200",
};
const STATUS_LABEL = {
  waiting_for_operator: "Waiting for Operator",
  running: "Running",
  retrying: "Retrying",
  completed: "Completed",
  failed: "Failed",
  pending: "Pending",
};

export default function Automation() {
  return (
    <>
      <PageHeader title="Automation" subtitle="Workflow rules that orchestrate everything from webhook payments to Elitebase onboarding — turn them on or off, tweak options, and watch the queue in real time." />
      <PageBody>
        <Tabs defaultValue="runs" className="w-full">
          <TabsList className="mb-4 flex-wrap gap-1" data-testid="automation-tabs">
            <TabsTrigger value="runs" data-testid="tab-runs"><PlayCircle className="w-3.5 h-3.5 mr-1.5" />Active Runs</TabsTrigger>
            <TabsTrigger value="rules" data-testid="tab-rules"><Settings2 className="w-3.5 h-3.5 mr-1.5" />Workflow Rules</TabsTrigger>
          </TabsList>
          <TabsContent value="runs"><ActiveRunsTab /></TabsContent>
          <TabsContent value="rules"><WorkflowRulesTab /></TabsContent>
        </Tabs>
      </PageBody>
    </>
  );
}

// -------------------- Active Runs tab --------------------
function ActiveRunsTab() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (status !== "all") params.status = status;
      const { data } = await api.get("/automation/runs", { params });
      setRows(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const openRun = (row) => {
    // Gateway_transaction rows go to the existing 5-step wizard.
    if (row.source === "gateway_transaction") nav(`/action-required/${row.id}`);
    else setDetailRow(row);
  };
  const [detailRow, setDetailRow] = useState(null);

  const retry = async (row, ev) => {
    ev?.stopPropagation();
    setRetrying(row.id);
    try {
      await api.post(`/automation/runs/${row.id}/retry`);
      toast.success("Run queued for retry");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setRetrying(null); }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-2">
        <div className="w-56">
          <Label className="text-xs uppercase text-slate-500">Status</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="runs-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="waiting_for_operator">Waiting for operator</SelectItem>
              <SelectItem value="running">Running</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="runs-refresh"><RefreshCw className="w-4 h-4 mr-1.5" />Refresh</Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Workflow</TableHead>
            <TableHead>Trigger</TableHead>
            <TableHead>Student</TableHead>
            <TableHead>Current step</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Updated</TableHead>
            <TableHead className="text-right" />
          </TableRow></TableHeader>
          <TableBody data-testid="runs-body">
            {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-8">No automation runs match this filter.</TableCell></TableRow>}
            {rows.map((r) => (
              <TableRow key={r.id} onClick={() => openRun(r)} className="cursor-pointer" data-testid={`run-row-${r.id}`}>
                <TableCell><div className="font-medium">{r.name}</div><div className="text-[11px] text-slate-500 font-mono">{r.rule_code}</div></TableCell>
                <TableCell className="text-xs text-slate-600">{r.trigger}</TableCell>
                <TableCell className="text-xs">{r.student_name || <span className="text-slate-400">—</span>}</TableCell>
                <TableCell className="text-xs">{r.current_step || "—"}{r.total_steps && <span className="text-slate-400 ml-1">· {r.current_step_index || 0}/{r.total_steps}</span>}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={STATUS_STYLE[r.status] || STATUS_STYLE.pending} data-testid={`run-status-${r.id}`}>{STATUS_LABEL[r.status] || r.status}</Badge>
                  {r.failure_reason && <div className="text-[11px] text-rose-700 mt-1 max-w-xs truncate" title={r.failure_reason}>{r.failure_reason}</div>}
                </TableCell>
                <TableCell className="text-xs text-slate-500 font-mono">{fmtDateTime(r.updated_at)}</TableCell>
                <TableCell className="text-right">
                  {r.status === "failed" && (
                    <Button size="sm" variant="outline" onClick={(e) => retry(r, e)} disabled={retrying === r.id} data-testid={`run-retry-${r.id}`}>
                      {retrying === r.id ? "Retrying…" : "Retry"}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <RunDetailDialog row={detailRow} onOpenChange={(o) => !o && setDetailRow(null)} onChanged={load} />
    </div>
  );
}

function RunDetailDialog({ row, onOpenChange, onChanged }) {
  const open = !!row;
  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{row.name}</DialogTitle>
          <DialogDescription>Trigger: {row.trigger} · Student: {row.student_name || "—"}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={STATUS_STYLE[row.status] || STATUS_STYLE.pending}>{STATUS_LABEL[row.status] || row.status}</Badge>
            <span className="text-xs text-slate-500 font-mono">{fmtDateTime(row.updated_at)}</span>
          </div>
          {row.failure_reason && <div className="text-xs bg-rose-50 border border-rose-200 text-rose-800 rounded p-2" data-testid="run-detail-failure">{row.failure_reason}</div>}
          <div className="border border-slate-200 rounded p-3 bg-slate-50">
            <div className="text-[11px] font-semibold uppercase text-slate-500 mb-2">Step log</div>
            {(row.steps || []).length === 0 && <div className="text-xs text-slate-400">No steps recorded yet.</div>}
            <ol className="space-y-1">
              {(row.steps || []).map((s, i) => (
                <li key={`${s.name || "step"}-${i}`} className="flex items-start gap-2 text-xs">
                  {s.status === "completed" ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5" /> :
                   s.status === "failed" ? <AlertTriangle className="w-3.5 h-3.5 text-rose-600 mt-0.5" /> :
                   <Clock className="w-3.5 h-3.5 text-slate-400 mt-0.5" />}
                  <div className="flex-1">
                    <div className="font-medium">{s.name} <span className="font-mono text-slate-400 text-[10px]">{s.status}</span></div>
                    {s.message && <div className="text-slate-600">{s.message}</div>}
                  </div>
                  <div className="text-slate-400 font-mono text-[10px]">{fmtDateTime(s.at)}</div>
                </li>
              ))}
            </ol>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -------------------- Workflow Rules tab --------------------
function WorkflowRulesTab() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/automation/rules");
      setRules(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (rule, active) => {
    try {
      const { data } = await api.put(`/automation/rules/${rule.code}`, { active });
      setRules((r) => r.map((x) => x.code === rule.code ? data : x));
      toast.success(`${data.name} · ${data.active ? "activated" : "deactivated"}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  if (loading && rules.length === 0) return <div className="text-slate-500 text-sm">Loading workflow rules…</div>;

  return (
    <div className="space-y-3">
      {rules.map((rule) => (
        <div key={rule.code} className="bg-white border border-slate-200 rounded-md p-5 shadow-sm" data-testid={`rule-${rule.code}`}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-[300px]">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-base font-semibold text-slate-900">{rule.name}</h3>
                <Badge variant="outline" className={rule.active ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-slate-100 text-slate-600"} data-testid={`rule-status-${rule.code}`}>
                  {rule.active ? "Active" : "Inactive"}
                </Badge>
              </div>
              <p className="text-sm text-slate-600 mt-1">{rule.description}</p>
              <p className="text-xs text-slate-500 mt-2"><span className="font-semibold uppercase">Trigger:</span> {rule.trigger}</p>
              <ol className="text-xs text-slate-600 mt-2 list-decimal pl-4 space-y-0.5">
                {(rule.steps || []).map((s, i) => <li key={`${rule.code}-step-${i}`}>{s}</li>)}
              </ol>
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="flex items-center gap-2">
                <Label className="text-xs uppercase text-slate-500">Active</Label>
                <Switch checked={rule.active} onCheckedChange={(v) => toggle(rule, v)} data-testid={`rule-toggle-${rule.code}`} />
              </div>
              <Button variant="outline" size="sm" onClick={() => setEditing(rule)} data-testid={`rule-configure-${rule.code}`}>
                <Settings2 className="w-3.5 h-3.5 mr-1.5" />Configure
              </Button>
            </div>
          </div>
        </div>
      ))}
      <ConfigureRuleDialog rule={editing} onOpenChange={(o) => !o && setEditing(null)} onSaved={(r) => { setRules((all) => all.map((x) => x.code === r.code ? r : x)); setEditing(null); }} />
    </div>
  );
}

function ConfigureRuleDialog({ rule, onOpenChange, onSaved }) {
  const [config, setConfig] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { setConfig(rule ? { ...rule.config } : {}); }, [rule]);
  if (!rule) return null;

  const schema = rule.config_schema || {};

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/automation/rules/${rule.code}`, { config });
      toast.success("Configuration saved. Effective on the next trigger.");
      onSaved(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!rule} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Configure · {rule.name}</DialogTitle>
          <DialogDescription>Changes take effect on the next trigger.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {Object.entries(schema).map(([key, def]) => (
            <ConfigField key={key} field_key={key} def={def}
                          value={config[key]}
                          onChange={(v) => setConfig({ ...config, [key]: v })} />
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="rule-config-save">{saving ? "Saving…" : "Save configuration"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConfigField({ field_key, def, value, onChange }) {
  if (def.type === "bool") {
    return (
      <div className="flex items-center justify-between gap-4 border border-slate-200 rounded p-3">
        <div className="flex-1">
          <Label className="text-sm">{def.label}</Label>
        </div>
        <Switch checked={!!value} onCheckedChange={onChange} data-testid={`cfg-${field_key}`} />
      </div>
    );
  }
  if (def.type === "select") {
    return (
      <div>
        <Label className="text-xs uppercase text-slate-500">{def.label}</Label>
        <Select value={value || ""} onValueChange={onChange}>
          <SelectTrigger data-testid={`cfg-${field_key}`}><SelectValue placeholder="Choose…" /></SelectTrigger>
          <SelectContent>
            {(def.options || []).map(([v, label]) => <SelectItem key={v} value={v}>{label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    );
  }
  if (def.type === "int" || def.type === "int_or_null") {
    return (
      <div>
        <Label className="text-xs uppercase text-slate-500">{def.label}</Label>
        <Input type="number" min={def.min} max={def.max} value={value ?? ""}
                onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
                data-testid={`cfg-${field_key}`} />
      </div>
    );
  }
  if (def.type === "time") {
    return (
      <div>
        <Label className="text-xs uppercase text-slate-500">{def.label}</Label>
        <Input type="time" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`cfg-${field_key}`} />
      </div>
    );
  }
  // email / text fallback
  return (
    <div>
      <Label className="text-xs uppercase text-slate-500">{def.label}</Label>
      <Input type={def.type === "email" ? "email" : "text"} value={value || ""}
              onChange={(e) => onChange(e.target.value)} data-testid={`cfg-${field_key}`} />
    </div>
  );
}
